"use client";

import React, { useState } from "react";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  name: "18-Hole Walkthrough",
  priceMonthly: 2500,
  priceAnnual: 2000,
  description: "Perfect for courses building their digital presence. Professional hole-by-hole coverage that attracts golfers and showcases your course design.",
  features: ["4K Drone Flyovers", "Hole-by-Hole Editing", "FAA Part 107 Compliant", "Arizona Specialist Team", "Standard Turnaround"],
  isPopular: false,
  buttonText: "Select Package",
  buttonLink: "/RequestAQuotePage"
}, {
  name: "Full Experience",
  priceMonthly: 4500,
  priceAnnual: 3600,
  description: "Complete media solution for courses serious about marketing. Everything you need to elevate visibility, attract members, and showcase your unique character.",
  features: ["18-Hole 4K Flyovers", "Cinematic Clubhouse Video", "Aerial Photography Suite", "Yardage & Hazard Graphics", "Voiceover Narration", "Priority Rush Editing"],
  isPopular: true,
  buttonText: "Select Package",
  buttonLink: "/RequestAQuotePage"
}, {
  name: "Cinematic Club",
  priceMonthly: 2500,
  priceAnnual: 2000,
  description: "Ideal for highlighting clubhouse amenities and lifestyle. Create compelling social media content and promotional videos that reflect your course's personality.",
  features: ["Lifestyle Drone Shots", "Interior Clubhouse Video", "Social Media Teasers", "FAA Part 107 Compliant", "Fast Turnaround", "Aerial Photography"],
  isPopular: false,
  buttonText: "Select Package",
  buttonLink: "/RequestAQuotePage"
}];
export default function ChooseYourPackage() {
  const [isAnnual, setIsAnnual] = useState(false);
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  const {
    ref: cardsRef,
    isVisible: areCardsVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224509" id="signature-experiences" className="bg-background text-foreground py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div ref={headerRef} className={`flex flex-col items-center text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-6xl font-bold font-default tracking-tight mb-6 text-foreground">
            Signature Experiences
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl font-default">
            Three thoughtfully designed packages, each focused on delivering measurable marketing outcomes for your course. Add premium enhancements or go fully custom—the choice is yours.
          </p>

          <div className="flex items-center gap-4 bg-secondary/50 p-2 rounded-lg border border-border">
            <span className={`text-sm font-medium transition-colors font-default ${!isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Standard
            </span>
            <Switch checked={isAnnual} onCheckedChange={setIsAnnual} className="data-[state=checked]:bg-primary" />
            <span className={`text-sm font-medium transition-colors flex items-center gap-2 font-default ${isAnnual ? "text-foreground" : "text-muted-foreground"}`}>
              Annual Refresh
              <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 border-none font-default">
                Save 20%
              </Badge>
            </span>
          </div>
        </div>

        <div ref={cardsRef} className={`grid grid-cols-1 md:grid-cols-3 gap-10 items-center transition-all duration-1000 ease-out ${areCardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {plans.map((plan, i) => {
          const price = isAnnual ? plan.priceAnnual : plan.priceMonthly;
          return <div key={plan.name} data-index={i} className={`relative flex flex-col p-10 rounded-md bg-card border transition-all duration-300 ${plan.isPopular ? "border-primary shadow-2xl shadow-primary/10 md:-translate-y-4 md:scale-105 z-10" : "border-border shadow-md hover:shadow-lg"}`}>
                {plan.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center">
                    <Badge className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-1 text-sm rounded-md shadow-md font-default uppercase tracking-widest">
                      Most Popular
                    </Badge>
                  </div>}

                <div className="mb-8">
                  <h3 className="text-xl font-semibold font-default mb-2 text-foreground">{plan.name}</h3>
                  <p className="text-sm text-muted-foreground min-h-[40px] font-default">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-8 flex items-baseline text-foreground">
                  <span className="text-xl font-medium mr-1 font-default text-primary">$</span>
                  <span className="text-5xl font-bold font-default tracking-tight text-primary">
                    {price.toLocaleString()}
                  </span>
                  <span className="text-muted-foreground ml-2 font-default">starting</span>
                </div>

                <div className="flex-grow">
                  <ul className="space-y-6 mb-8">
                    {plan.features.map((feature, idx) => <li key={`feat-${idx}`} data-index={idx} className="flex items-start gap-3">
                        <Check className={`size-5 shrink-0 ${plan.isPopular ? "text-primary" : "text-primary"}`} />
                        <span className="text-sm text-foreground font-default">{feature}</span>
                      </li>)}
                  </ul>
                </div>

                <Button asChild size="lg" className={`w-full rounded-md font-semibold transition-all font-default uppercase tracking-wider ${plan.isPopular ? "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/20" : "bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-primary/20"}`}>
                  <Link to={plan.buttonLink}>{plan.buttonText}</Link>
                </Button>
              </div>;
        })}
        </div>
      </div>
    </section>;
}